import type { EmailComposeAttachment } from '@elva/ui';
import { postFormData } from '@/api/client';
import type { EmailAttachmentUploadResponse } from './types';

export interface MailAttachmentWire {
  filename: string;
  content_type: string;
  content_base64: string;
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error(`Could not read ${file.name}.`));
        return;
      }
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error(`Could not read ${file.name}.`));
    };
    reader.readAsDataURL(file);
  });
}

/** @deprecated Prefer uploadEmailAttachmentHandles + legacy reply/compose. */
export async function encodeMailAttachments(
  attachments: EmailComposeAttachment[] | undefined,
): Promise<MailAttachmentWire[]> {
  if (!attachments?.length) return [];

  return Promise.all(
    attachments.map(async ({ file }) => ({
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
      content_base64: await readFileAsBase64(file),
    })),
  );
}

export function readAttachmentUploadHandle(data: unknown): string {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid attachment upload response.');
  }

  const row = data as EmailAttachmentUploadResponse & Record<string, unknown>;
  for (const key of ['handle', 'attachment_handle', 'upload_handle', 'id'] as const) {
    const value = row[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }

  for (const nestedKey of ['attachment', 'data', 'result'] as const) {
    const nested = row[nestedKey];
    if (nested && typeof nested === 'object') {
      try {
        return readAttachmentUploadHandle(nested);
      } catch {
        // try next nested key
      }
    }
  }

  throw new Error('Attachment upload response missing handle.');
}

export async function uploadEmailAttachment(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('file', file, file.name);
  const data = await postFormData<EmailAttachmentUploadResponse>(
    '/api/email/attachments/upload',
    formData,
  );
  return readAttachmentUploadHandle(data);
}

export async function uploadEmailAttachmentHandles(
  attachments: EmailComposeAttachment[] | undefined,
): Promise<string[]> {
  if (!attachments?.length) return [];
  return Promise.all(attachments.map(({ file }) => uploadEmailAttachment(file)));
}
